import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import useSEO from "@/hooks/useSEO";
import {
  LogOut,
  LogIn,
  RefreshCcw,
  MessageCircle,
  Mail,
  Phone,
  Link as LinkIcon,
  Trash2,
  ShieldCheck,
  Search,
  Sparkles,
  Loader2,
  Zap,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "isr_admin_token";

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function LoginScreen({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(STORAGE_KEY, data.token);
      toast.success("Welcome back");
      onSuccess(data.token);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-screen"
      className="min-h-screen bg-[#0B0F19] text-white grid place-items-center px-5 hero-grid-bg"
    >
      <div className="absolute inset-0 hero-grid pointer-events-none" />
      <div className="absolute -top-40 -left-40 h-[420px] w-[420px] rounded-full bg-blue-600/20 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 h-[420px] w-[420px] rounded-full bg-indigo-600/20 blur-[120px] pointer-events-none" />

      <form
        onSubmit={submit}
        className="relative w-full max-w-md rounded-3xl glass-dark p-8 sm:p-10 shadow-[0_30px_120px_-20px_rgba(59,130,246,0.35)]"
      >
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-yellow-400 text-slate-900 shadow-lg">
            <ShieldCheck size={20} />
          </span>
          <div>
            <div className="text-xs font-black tracking-widest text-yellow-300 uppercase">
              Admin Access
            </div>
            <div className="font-display font-extrabold text-xl">Instant Site Revival</div>
          </div>
        </div>
        <h1 className="mt-6 font-display text-2xl sm:text-3xl font-extrabold">
          Enter your admin password
        </h1>
        <p className="mt-2 text-sm text-slate-400">
          Private dashboard for viewing lead submissions.
        </p>
        <div className="mt-6">
          <label htmlFor="admin-password" className="text-xs font-bold tracking-widest uppercase text-slate-400">
            Password
          </label>
          <input
            id="admin-password"
            type="password"
            autoComplete="current-password"
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="mt-2 w-full h-12 rounded-xl bg-white/5 border border-white/10 px-4 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60 focus:border-transparent"
            required
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-btn"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 text-slate-900 font-black px-6 py-3.5 transition-all hover:-translate-y-0.5 glow-gold"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
          {loading ? "Signing in..." : "Sign In"}
        </button>
        <a
          href="/"
          className="mt-4 block text-center text-xs text-slate-500 hover:text-slate-300"
        >
          ← Back to landing page
        </a>
      </form>
    </div>
  );
}

function LeadCard({ lead, onDelete, kind = "website" }) {
  const phoneDigits = (lead.phone || "").replace(/[^0-9]/g, "");
  const waLink = phoneDigits
    ? `https://wa.me/${phoneDigits.startsWith("1") ? phoneDigits : "1" + phoneDigits}?text=Hi%20${encodeURIComponent(
        lead.name || ""
      )}%21%20Thanks%20for%20reaching%20out%20to%20Instant%20Site%20Revival.`
    : null;
  const url = lead.current_url || lead.website_url || "";
  const social = lead.social_url || "";
  const extra = lead.website_type || lead.service_interest || "";
  const accent = kind === "social" ? "text-cyan-600" : "text-blue-600";
  return (
    <div
      data-testid={`admin-lead-${lead.id}`}
      className="rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`text-xs font-black uppercase tracking-widest ${accent}`}>
            {lead.business_name || "—"}
          </div>
          <div className="mt-1 font-display font-bold text-lg text-slate-900 truncate">
            {lead.name}
          </div>
          <div className="text-xs text-slate-500 mt-1">{fmtDate(lead.created_at)}</div>
        </div>
        <button
          onClick={() => onDelete(lead)}
          data-testid={`admin-delete-${lead.id}`}
          className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-xl border border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-300 transition"
          title="Delete lead"
        >
          <Trash2 size={16} />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-2 text-sm">
        <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-700">
          <Phone size={14} className="text-slate-400" /> {lead.phone}
        </a>
        <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-700 truncate">
          <Mail size={14} className="text-slate-400" /> {lead.email}
        </a>
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-700 hover:text-blue-700 truncate">
            <LinkIcon size={14} className="text-slate-400" /> {url}
          </a>
        ) : null}
        {social ? (
          <a href={social} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-700 hover:text-cyan-700 truncate">
            <Sparkles size={14} className="text-cyan-500" /> {social}
          </a>
        ) : null}
        {extra ? (
          <div className="flex items-center gap-2 text-slate-700">
            <Sparkles size={14} className="text-slate-400" /> {extra}
          </div>
        ) : null}
      </div>

      {lead.message ? (
        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-100 p-3 text-sm text-slate-700 whitespace-pre-wrap">
          {lead.message}
        </div>
      ) : null}

      <div className="mt-5 flex flex-wrap gap-2">
        {waLink && (
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`admin-wa-${lead.id}`}
            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1eb558] text-white font-bold px-4 py-2 text-xs transition"
          >
            <MessageCircle size={14} /> WhatsApp
          </a>
        )}
        <a
          href={`mailto:${lead.email}?subject=Re%3A%20Your%20Instant%20Site%20Revival%20Request&body=Hi%20${encodeURIComponent(
            lead.name || ""
          )}%2C%0A%0AThanks%20for%20reaching%20out.%0A%0A`}
          data-testid={`admin-email-${lead.id}`}
          className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-bold px-4 py-2 text-xs transition"
        >
          <Mail size={14} /> Email
        </a>
      </div>
    </div>
  );
}

function AdminDashboard({ token, onLogout }) {
  const [tab, setTab] = useState("website");
  const [websiteLeads, setWebsiteLeads] = useState([]);
  const [socialLeads, setSocialLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const [web, soc] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers: { "X-Admin-Token": token } }),
        axios.get(`${API}/admin/social-leads`, { headers: { "X-Admin-Token": token } }),
      ]);
      setWebsiteLeads(web.data);
      setSocialLeads(soc.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("Session expired. Please sign in again.");
        sessionStorage.removeItem(STORAGE_KEY);
        onLogout();
      } else {
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const onDelete = async (lead) => {
    if (!window.confirm(`Delete lead from ${lead.name}? This cannot be undone.`)) return;
    try {
      const path = tab === "website" ? "leads" : "social-leads";
      await axios.delete(`${API}/admin/${path}/${lead.id}`, {
        headers: { "X-Admin-Token": token },
      });
      if (tab === "website") setWebsiteLeads((prev) => prev.filter((l) => l.id !== lead.id));
      else setSocialLeads((prev) => prev.filter((l) => l.id !== lead.id));
      toast.success("Lead deleted");
    } catch {
      toast.error("Failed to delete lead");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    toast.success("Signed out");
    onLogout();
  };

  const [zapTesting, setZapTesting] = useState(false);
  const testZapier = async () => {
    setZapTesting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/webhook/test`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      if (!data.configured) {
        toast.error("No Zapier webhook configured. Set ZAPIER_WEBHOOK_URL in backend/.env.");
      } else {
        toast.success(`Test payload sent to ${data.hooks} Zapier hook${data.hooks === 1 ? "" : "s"}.`);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("Session expired. Please sign in again.");
        sessionStorage.removeItem(STORAGE_KEY);
        onLogout();
      } else {
        toast.error("Failed to send test payload");
      }
    } finally {
      setZapTesting(false);
    }
  };

  const activeLeads = tab === "website" ? websiteLeads : socialLeads;
  const filtered = activeLeads.filter((l) => {
    if (!q.trim()) return true;
    const hay = `${l.name} ${l.business_name} ${l.email} ${l.phone} ${l.website_type || ""} ${l.service_interest || ""} ${l.social_url || ""} ${l.current_url || l.website_url || ""} ${l.message}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-[#0B0F19] text-white border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white shadow overflow-hidden">
              <img src="/logo-icon.png" alt="" className="h-8 w-8 object-contain" />
            </span>
            <div className="min-w-0">
              <div className="font-display font-extrabold truncate">
                Instant Site<span className="text-yellow-400"> Revival</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">
                Lead Dashboard
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={testZapier}
              data-testid="admin-test-zapier-btn"
              disabled={zapTesting}
              title="Send a sample lead to your Zapier webhook"
              className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 text-white font-bold px-4 py-2 text-xs transition disabled:opacity-60"
            >
              {zapTesting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Zap size={14} className="text-yellow-300" />
              )}
              <span className="hidden sm:inline">Test Zapier</span>
            </button>
            <button
              onClick={fetchLeads}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 text-white font-bold px-4 py-2 text-xs transition"
              disabled={loading}
            >
              <RefreshCcw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold px-4 py-2 text-xs transition"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2" data-testid="admin-tabs">
          <button
            onClick={() => setTab("website")}
            data-testid="admin-tab-website"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition ${
              tab === "website"
                ? "bg-yellow-400 text-slate-900 shadow"
                : "bg-white text-slate-700 border border-slate-200 hover:border-yellow-400"
            }`}
          >
            Website Leads
            <span className={`text-[11px] font-black px-2 py-0.5 rounded-full ${tab === "website" ? "bg-slate-900 text-yellow-300" : "bg-slate-100 text-slate-600"}`}>
              {websiteLeads.length}
            </span>
          </button>
          <button
            onClick={() => setTab("social")}
            data-testid="admin-tab-social"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition ${
              tab === "social"
                ? "bg-cyan-400 text-slate-900 shadow"
                : "bg-white text-slate-700 border border-slate-200 hover:border-cyan-400"
            }`}
          >
            Social Leads
            <span className={`text-[11px] font-black px-2 py-0.5 rounded-full ${tab === "social" ? "bg-slate-900 text-cyan-300" : "bg-slate-100 text-slate-600"}`}>
              {socialLeads.length}
            </span>
          </button>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">
              {filtered.length} {tab === "website" ? "website" : "social"} lead{filtered.length === 1 ? "" : "s"}
            </h1>
            <p className="text-slate-600 mt-1">Most recent first. Reply, delete, or filter below.</p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, business, email..."
              data-testid="admin-search"
              className="w-full h-11 pl-9 pr-3 rounded-full bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
            />
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="py-24 text-center text-slate-500 flex flex-col items-center gap-3">
              <Loader2 size={24} className="animate-spin text-blue-500" />
              Loading leads...
            </div>
          ) : filtered.length === 0 ? (
            <div
              data-testid="admin-empty-state"
              className="py-24 text-center text-slate-500 rounded-3xl bg-white border border-dashed border-slate-200"
            >
              {q ? "No leads match your search." : "No leads yet — share your landing page to start collecting."}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map((lead) => (
                <LeadCard key={lead.id} lead={lead} onDelete={onDelete} kind={tab} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  useSEO({
    title: "Admin · Instant Site Revival",
    description: "Private lead dashboard.",
    noindex: true,
    path: "/admin",
  });
  const [token, setToken] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");

  return (
    <>
      {token ? (
        <AdminDashboard token={token} onLogout={() => setToken("")} />
      ) : (
        <LoginScreen onSuccess={(t) => setToken(t)} />
      )}
      <Toaster richColors position="top-center" />
    </>
  );
}
